import { FieldSelect } from '@components/field-builder/field-select';
import { ModalContainer } from '@components/modal/modal';
import { DeclineReason } from '@dtos/remote-i9-decline';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { ClientTrackingName } from '@models/client-tracking-name';
import { FieldType } from '@models/field-builder/field-type';
import { LabelPosition } from '@models/field-builder/label-position';
import {
    SpacedText
} from '@screens/review-section3/review/styles';
import {
    createTrackingService,
    createTranslationService
} from '@services/factories';
import { StringFormatService } from '@services/StringFormatService';
import { IDeclineModalStore } from '@stores/modal/decline-modal-store';
import { cacheTranslations } from '@utils/cache-translations';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
    DeclineModalContainer,
    OtherReasonTextArea
} from './styles';
import { ValidationErrorContainer } from '@screens/review-section3/review/validation-error-message';

interface IProps {
    store: IDeclineModalStore;
    trackingCategory: ClientTrackingCategory;
    onDeclineSuccess?: () => void;
    /**
     * Context insert for "Please tell us why you are declining to complete the {context} of the I-9:"
     * Explains the context of what is being declined
     */
    declineQuestionContext: string;
}

enum DeclineModalTranslation {
    declineModalTitle = 'declineModalTitle',
    declineQuestion = 'declineQuestion',
    cancelButton = 'cancelButton',
    submitButton = 'submitButton',
    declineErrorMessage = 'declineErrorMessage',
    requestFailure = 'requestFailure',
    selectLabel = 'selectLabel',
    declineReasonCannotExamineDoc = 'declineReasonCannotExamineDoc',
    declineReasonIllegalDoc = 'declineReasonIllegalDoc',
    declineReasonInvalidDocument = 'declineReasonInvalidDocument',
    declineReasonIncorrectInfo = 'declineReasonIncorrectInfo',
    declineReasonNotComfortableSigning = 'declineReasonNotComfortableSigning',
    declineReasonIllegalRepresentative = 'declineReasonIllegalRepresentative',
    declineReasonOther = 'declineReasonOther'
}

@observer
class DeclineModal extends React.Component<IProps> {
    // Probably good idea to dependency inject these later
    trackingService = createTrackingService();

    private readonly translations = cacheTranslations(
        createTranslationService(),
        DeclineModalTranslation
    );

    handleConfirmClicked = async () => {
        const {
            trackingCategory,
            store,
            onDeclineSuccess
        } = this.props;
        const result = await store.requestDeclineAsync();
        if (result) {
            this.trackingService.trackClickEvent(trackingCategory, ClientTrackingName.Success);
            onDeclineSuccess?.();
        } else {
            this.trackingService.trackClickEvent(trackingCategory, ClientTrackingName.Error);
        }
    };

    constructor(props) {
        super(props);
    }

    handleOtherReasonTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        event.preventDefault();
        this.props.store.setReasonDescription(event.target.value);
    };

    handleReasonSelected = (reason: DeclineReason) => {
        this.props.store.selectReason(reason);
    };

    renderModalContent() {
        const {
            store
        } = this.props;

        const declineReasonOptions = [
            {
                key: DeclineReason.CANNOT_EXAMINE_PHYSICAL_DOCUMENT,
                label: this.translations.declineReasonCannotExamineDoc
            },
            {
                key: DeclineReason.ILLEGAL_DOCUMENT,
                label: this.translations.declineReasonIllegalDoc
            },
            {
                key: DeclineReason.INVALID_DOCUMENT,
                label: this.translations.declineReasonInvalidDocument
            },
            {
                key: DeclineReason.INCORRECT_INFORMATION,
                label: this.translations.declineReasonIncorrectInfo
            },
            {
                key: DeclineReason.NOT_COMFORTABLE_SIGNING,
                label: this.translations.declineReasonNotComfortableSigning
            },
            {
                key: DeclineReason.ILLEGAL_AUTHORIZED_REPRESENTATIVE,
                label: this.translations.declineReasonIllegalRepresentative
            },
            {
                key: DeclineReason.OTHER,
                label: this.translations.declineReasonOther
            }
        ];

        const validationError = !store.wasValid ?
            <ValidationErrorContainer>
                {this.translations.declineErrorMessage}
            </ValidationErrorContainer> : null;
        const requestError = store.hadErrorDeclining ?
            <ValidationErrorContainer>
                {this.translations.requestFailure}
            </ValidationErrorContainer> : null;

        return (
            <DeclineModalContainer>
                <SpacedText>
                    {
                        StringFormatService.format(
                            this.translations.declineQuestion,
                            [this.props.declineQuestionContext]
                        )
                    }
                </SpacedText>
                <FieldSelect labelPosition={LabelPosition.TOP}
                             placeHolder={this.translations.selectLabel}
                             id={'decline-reason'}
                             type={FieldType.SELECT}
                             setValueMethod={this.handleReasonSelected}
                             value={store.selectedReason}
                             options={declineReasonOptions} />
                <OtherReasonTextArea value={store.reasonDescription}
                                     onChange={this.handleOtherReasonTextChange}/>
                {validationError}
                {requestError}
            </DeclineModalContainer>
        );
    }

    render() {
        const {
            store
        } = this.props;
        const modalContent = this.renderModalContent();

        if (!store.showModal) {
            return null;
        }

        return (
            <ModalContainer
                title={this.translations.declineModalTitle}
                isOpen={store.showModal}
                onCancelClickHandler={() => store.closeModal()}
                onConfirmClickHandler={this.handleConfirmClicked}
                cancelLabel={this.translations.cancelButton}
                confirmLabel={this.translations.submitButton}>
                {modalContent}
            </ModalContainer>
        );
    }
}

export default DeclineModal;



// WEBPACK FOOTER //
// ./src/containers/decline-modal/index.tsx